/**
 * Impostazioni sul telefono (7/10/2026): risponde a "chi entra nel
 * gestionale, e i servizi collegati funzionano?". Sono le impostazioni
 * dell'iPhone: in cima l'organizzazione, sotto un elenco di voci, e ogni voce
 * apre la sua pagina (?voce=…, così la freccia indietro del telefono torna
 * all'elenco).
 *
 * Prima, a 390px: il menu a due colonne del computer dentro una scheda, poi
 * "Filtri" da solo su una riga, due avvisi "si fa dal computer" e gli utenti
 * come schede RUOLO / STATO. Le integrazioni erano tre riquadri di numeri e
 * la matrice dei permessi non c'era.
 * Qui:
 *   1. l'organizzazione, con quante persone e se i collegamenti vanno;
 *   2. Persone e accessi: gli utenti (ognuno con la sua pagina) e i ruoli,
 *      con cosa vede ogni ruolo modulo per modulo: è la matrice del computer
 *      letta per colonna, che sul telefono ci sta;
 *   3. Servizi collegati: FattureInCloud, Qonto e gli altri, col loro stato;
 *   4. Organizzazione: le preferenze.
 * Si modifica come sull'iPhone: la riga mostra il valore, si tocca, sale un
 * elenco di scelte (MScelte) o un campo solo con Salva (MCampo). Mai un
 * modulo di campi in fila. Restano al computer le cose nuove (utente, ruolo,
 * collegamento) e collegare o scollegare un servizio: servono chiavi da
 * incollare. Stesse chiamate e stesse chiavi di cache del computer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { useUrlState } from '../../hooks/useUrlState';
import { Icon } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { MCampo, MEmpty, MGroup, MHead, MPage, MRow, MScelte, MSearch, iniziali } from '../../components/mobile/MobileKit';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import './settings-mobile.css';

interface Role {
  id: string; key: string | null; name: string; color: string; shortLabel: string;
  canSeeMargin: boolean; isSystem: boolean; usersCount: number;
  permissions: Array<{ module: string; level: string }>;
}
interface UserItem {
  id: string; email: string; firstName: string; lastName: string;
  initials: string; avatarColor: string; active: boolean;
  role: { id: string; name: string; color: string; shortLabel: string; key: string | null };
}
interface OrgSettings {
  organizationName: string; logoUrl: string | null; primaryColor: string;
  marginThreshold: string; currency: string; timezone: string;
  dateFormat: string; docLanguage: string; fiscalYearStart: number;
}
interface Integration {
  id: string; kind: string; name: string; status: string;
  config: any; lastSyncAt: string | null; hasKey: boolean;
  connectedBy: { firstName: string; lastName: string } | null;
}
interface FicStatus { connected: boolean; companyId: number | null; companyName: string | null; connectedAt: string | null; lastSyncAt: string | null }
interface QontoStatus {
  connected: boolean; organizationName?: string | null; orgSlug?: string | null; login?: string | null;
  connectedAt?: string | null; lastSyncAt?: string | null; accounts?: Array<{ id: string; name: string; balance: number }> | null;
}

// Le stesse chiavi e opzioni della pagina del computer: una cache sola.
const ficStatusQuery = { queryKey: ['fic-status'] as const, queryFn: () => api.get<FicStatus>('/settings/integrations/fic/status'), refetchOnWindowFocus: false };
const qontoStatusQuery = { queryKey: ['qonto-status'] as const, queryFn: () => api.get<QontoStatus>('/settings/integrations/qonto/status'), refetchOnWindowFocus: false, retry: 1 };
const rolesQuery = { queryKey: ['roles'] as const, queryFn: () => api.get<Role[]>('/roles') };
const usersAllQuery = { queryKey: ['users', 'stats'] as const, queryFn: () => api.get<{ items: UserItem[] }>('/users?pageSize=200') };
const orgQuery = { queryKey: ['org-settings'] as const, queryFn: () => api.get<OrgSettings>('/settings/org') };

type Voce = 'home' | 'utenti' | 'ruoli' | 'preferenze' | 'integrazioni';

/** I moduli nell'ordine e coi nomi della barra laterale (sono i sedici della matrice del computer). */
const MODULI: Array<[string, string]> = [
  ['DASHBOARD', 'Oggi'], ['TASKS', 'Task'], ['TRATTATIVE', 'Trattative'], ['CRM', 'Contatti e progetti'],
  ['AMMINISTRAZIONE', 'Amministrazione'], ['FATTURAZIONE', 'Fatture e preventivi'], ['SERVICES', 'Abbonamenti'],
  ['MARKETING', 'Digital Marketing'], ['PRODUCT', 'Digital Product'], ['DESIGN', 'Digital Design'],
  ['TALENT', 'Talent Sourcing'], ['SUPPLIERS', 'Professionisti'], ['VAULT', 'Password Vault'], ['REPORT', 'Report'],
  ['SETTINGS', 'Impostazioni'], ['MARGIN', 'Margini'],
];
const LIVELLI: Array<{ v: string; l: string; sub: string }> = [
  { v: 'NONE', l: 'Non vede', sub: 'la sezione non compare' },
  { v: 'READ', l: 'Vede', sub: 'solo lettura' },
  { v: 'WRITE', l: 'Vede e modifica', sub: 'crea e modifica, non elimina i dati condivisi' },
  { v: 'ADMIN', l: 'Gestisce tutto', sub: 'pieno controllo, anche eliminare' },
];
const nomeLivello = (v: string) => LIVELLI.find((x) => x.v === v)?.l ?? v;

const KIND: Record<string, string> = {
  FATTURA24: 'Fattura24', SLACK: 'Slack', FIGMA: 'Figma', GITHUB: 'GitHub', META_ADS: 'Meta Ads',
  GOOGLE_CALENDAR: 'Google Calendar', ZOOM: 'Zoom', HARVEST: 'Harvest', OTHER: 'Altro',
};
const STATO_INT: Record<string, string> = { ACTIVE: 'collegata', ERROR: 'in errore', DISCONNECTED: 'scollegata' };
const MESI = Array.from({ length: 12 }, (_, i) => new Date(2026, i, 1).toLocaleDateString('it-IT', { month: 'long' }));

const giorno = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }).replace('.', '') : null);
/** "oggi alle 12:15", "il 6 ott": quando è stato aggiornato un collegamento. */
function quando(iso: string | null | undefined): string {
  if (!iso) return 'mai';
  const d = new Date(iso);
  if (d.toDateString() === new Date().toDateString()) return `oggi alle ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  return `il ${giorno(iso)}`;
}

/** Chi può modificare le impostazioni: lo stesso permesso che chiede l'API. */
function useLivelloImpostazioni() {
  const user = useAuthStore((s) => s.user);
  const lvl = user?.permissions.find((p) => p.module === ModuleKey.SETTINGS)?.level ?? PermissionLevel.NONE;
  return { user, scrive: hasAtLeast(lvl, PermissionLevel.WRITE), admin: hasAtLeast(lvl, PermissionLevel.ADMIN) };
}

export function SettingsMobile() {
  const [voceParam, setVoce] = useUrlState<string>('voce', 'home');
  const voce: Voce = (['utenti', 'ruoli', 'preferenze', 'integrazioni'] as string[]).includes(voceParam) ? (voceParam as Voce) : 'home';
  const [utente, setUtente] = useUrlState<string>('utente', '');
  const [ruolo, setRuolo] = useUrlState<string>('ruolo', '');
  const location = useLocation();
  const navigate = useNavigate();
  // Come il "<" della barra in alto: si torna da dove si è venuti; aperta da
  // un link (niente prima), si torna alla pagina sopra.
  const indietro = (sopra: () => void) => (location.key !== 'default' ? navigate(-1) : sopra());

  // Aprendo una voce si riparte dall'alto.
  useEffect(() => { document.querySelector('.os-main')?.scrollTo({ top: 0 }); }, [voce, utente, ruolo]);

  let corpo: ReactNode;
  let sopra = 'Impostazioni';
  if (voce === 'utenti' && utente) { corpo = <Utente id={utente} />; sopra = 'Utenti'; }
  else if (voce === 'ruoli' && ruolo) { corpo = <Ruolo id={ruolo} />; sopra = 'Ruoli e permessi'; }
  else if (voce === 'utenti') corpo = <Utenti onApri={setUtente} />;
  else if (voce === 'ruoli') corpo = <Ruoli onApri={setRuolo} />;
  else if (voce === 'preferenze') corpo = <Preferenze />;
  else if (voce === 'integrazioni') corpo = <Integrazioni />;

  if (voce === 'home') return <div className="stm"><Home onVoce={setVoce} /></div>;
  return (
    <div className="stm">
      <MPage>
        {/* Impostazioni è una sezione, non una scheda: la freccia della barra
            in alto qui non c'è, e il ritorno sta in cima alla pagina, come il
            "‹ Impostazioni" dell'iPhone. */}
        <button
          type="button"
          className="stm-indietro"
          onClick={() => indietro(() => (utente ? setUtente('') : ruolo ? setRuolo('') : setVoce('home')))}
        >
          <Icon name="chevronL" size={16} /> {sopra}
        </button>
        {corpo}
      </MPage>
    </div>
  );
}

// ── La pagina principale ──────────────────────────────────────────────────

function Home({ onVoce }: { onVoce: (v: Voce) => void }) {
  const { data: org } = useQuery(orgQuery);
  const { data: utenti } = useQuery(usersAllQuery);
  const { data: ruoli } = useQuery(rolesQuery);
  const { data: altre } = useQuery({ queryKey: ['integrations'], queryFn: () => api.get<Integration[]>('/settings/integrations') });
  const { data: fic } = useQuery(ficStatusQuery);
  const { data: qonto } = useQuery(qontoStatusQuery);

  const tutti = utenti?.items ?? [];
  const attivi = tutti.filter((u) => u.active).length;
  const inErrore = (altre ?? []).filter((i) => i.status === 'ERROR');
  const scollegati = (fic && !fic.connected ? 1 : 0) + (qonto && !qonto.connected ? 1 : 0);
  const problemi = scollegati + inErrore.length;
  const nome = org?.organizationName ?? 'Organizzazione';

  return (
    <MPage>
      {/* L'organizzazione in cima, come l'account in cima alle impostazioni dell'iPhone. */}
      <div className="mk-list">
        <MRow
          text={iniziali(nome)}
          title={nome}
          sub={
            <>
              {tutti.length ? `${tutti.length === 1 ? '1 persona' : `${tutti.length} persone`} · ` : ''}
              {problemi > 0
                ? <span className="is-alert">{problemi === 1 ? '1 collegamento da sistemare' : `${problemi} collegamenti da sistemare`}</span>
                : fic && qonto ? 'collegamenti a posto' : 'controllo i collegamenti…'}
            </>
          }
          onClick={() => onVoce('preferenze')}
        />
      </div>

      <MGroup title="Persone e accessi">
        <MRow icon="users" title="Utenti" sub={tutti.length ? (attivi === tutti.length ? 'tutti attivi' : `${attivi} attivi`) : undefined} trail={tutti.length || undefined} trailTone="mute" onClick={() => onVoce('utenti')} />
        <MRow icon="lock" title="Ruoli e permessi" sub="chi vede cosa" trail={ruoli?.length || undefined} trailTone="mute" onClick={() => onVoce('ruoli')} />
      </MGroup>

      <MGroup title="Servizi collegati">
        <MRow
          icon="invoice"
          tone={fic && !fic.connected ? 'alert' : undefined}
          title="FattureInCloud"
          sub={!fic ? 'controllo…' : fic.connected ? `collegato · aggiornato ${quando(fic.lastSyncAt)}` : 'non collegato: le fatture non arrivano'}
          onClick={() => onVoce('integrazioni')}
        />
        <MRow
          icon="landmark"
          tone={qonto && !qonto.connected ? 'alert' : undefined}
          title="Qonto"
          sub={!qonto ? 'controllo…' : qonto.connected ? `collegato · aggiornato ${quando(qonto.lastSyncAt)}` : 'non collegato: saldi e movimenti non arrivano'}
          onClick={() => onVoce('integrazioni')}
        />
        {(altre ?? []).map((i) => (
          <MRow
            key={i.id}
            icon="link"
            tone={i.status === 'ERROR' ? 'alert' : undefined}
            title={i.name}
            sub={[STATO_INT[i.status] ?? i.status.toLowerCase(), i.lastSyncAt ? `aggiornata ${quando(i.lastSyncAt)}` : null].filter(Boolean).join(' · ')}
            onClick={() => onVoce('integrazioni')}
          />
        ))}
      </MGroup>

      <MGroup title="Organizzazione">
        <MRow icon="settings" title="Preferenze" sub={org ? `${org.currency} · ${org.timezone} · documenti in ${org.docLanguage === 'it' ? 'italiano' : 'inglese'}` : undefined} onClick={() => onVoce('preferenze')} />
      </MGroup>

      <DesktopOnly
        cosa="Creare un utente, un ruolo o un collegamento nuovo"
        perche="sono moduli da compilare con calma, con chiavi e password da incollare. Dal telefono si consulta tutto e si cambia un valore alla volta."
      ><></></DesktopOnly>
    </MPage>
  );
}

// ── Utenti ────────────────────────────────────────────────────────────────

function Utenti({ onApri }: { onApri: (id: string) => void }) {
  const [search, setSearch] = useState('');
  // Le stesse due chiamate del computer: la lista cercata e quella intera.
  const { data } = useQuery({
    queryKey: ['users', search],
    queryFn: () => api.get<{ items: UserItem[] }>(`/users?pageSize=200${search ? `&search=${encodeURIComponent(search)}` : ''}`),
  });
  const { data: tutti } = useQuery(usersAllQuery);
  const items = data?.items ?? [];
  const totale = tutti?.items.length ?? 0;
  const nAttivi = (tutti?.items ?? []).filter((u) => u.active).length;
  const riga = (u: UserItem) => (
    <MRow
      key={u.id}
      text={u.initials || iniziali(`${u.firstName} ${u.lastName}`)}
      round
      title={`${u.firstName} ${u.lastName}`}
      sub={`${u.role.name} · ${u.email}`}
      trail={u.active ? undefined : 'disattivato'}
      trailTone="mute"
      onClick={() => onApri(u.id)}
    />
  );
  return (
    <>
      <MHead title="Utenti" sub={`${totale === 1 ? '1 persona' : `${totale} persone`} · ${nAttivi === totale ? 'tutte attive' : `${nAttivi} attive`}`} />
      {totale > 6 && <MSearch value={search} onChange={setSearch} placeholder="Cerca un nome o un'email" />}
      <MGroup title="Attivi" count={items.filter((u) => u.active).length || undefined}>
        {items.filter((u) => u.active).map(riga)}
        {!items.some((u) => u.active) && <MEmpty>{search ? `Nessun utente con "${search}".` : 'Nessun utente attivo.'}</MEmpty>}
      </MGroup>
      {items.some((u) => !u.active) && (
        <MGroup title="Disattivati" count={items.filter((u) => !u.active).length}>{items.filter((u) => !u.active).map(riga)}</MGroup>
      )}
    </>
  );
}

type PannelloUtente = null | 'nome' | 'cognome' | 'email' | 'ruolo';

function Utente({ id }: { id: string }) {
  const qc = useQueryClient();
  const { user: io, scrive, admin } = useLivelloImpostazioni();
  const { data: tutti } = useQuery(usersAllQuery);
  const { data: roles } = useQuery(rolesQuery);
  const [aperto, setAperto] = useState<PannelloUtente>(null);
  const [bozza, setBozza] = useState('');
  const u = tutti?.items.find((x) => x.id === id);

  const salva = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.patch(`/users/${id}`, data),
    onSuccess: (_r, data) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['roles'] });
      setAperto(null);
      toast.ok('active' in data ? (data.active ? 'Utente attivato' : 'Utente disattivato') : 'Utente aggiornato');
    },
    onError: (e: any) => toast.alert('Utente non aggiornato', e?.message ?? 'Riprova'),
  });

  if (!tutti) return <MEmpty>Carico l'utente…</MEmpty>;
  if (!u) return <MEmpty>Questo utente non c'è più.</MEmpty>;
  // Il proprio ruolo e il proprio accesso non si toccano da qui: un tocco
  // sbagliato e si resta chiusi fuori, senza un computer per rimediare.
  const sonoIo = io?.id === u.id;
  const apri = (p: PannelloUtente, v: string) => { if (!scrive) return; setBozza(v); setAperto(p); };
  const campo = (tipo: 'text' | 'email') => (
    <input className="inp" type={tipo} autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label={aperto ?? ''} />
  );

  return (
    <>
      <MHead title={`${u.firstName} ${u.lastName}`} sub={`${u.role.name} · ${u.active ? 'attivo' : 'disattivato'}${sonoIo ? ' · sei tu' : ''}`} />

      <MGroup title="Anagrafica">
        <MRow title="Nome" trail={u.firstName} trailTone="mute" onClick={scrive ? () => apri('nome', u.firstName) : undefined} />
        <MRow title="Cognome" trail={u.lastName} trailTone="mute" onClick={scrive ? () => apri('cognome', u.lastName) : undefined} />
        <MRow title="Email" sub={u.email} onClick={scrive ? () => apri('email', u.email) : undefined} />
      </MGroup>

      <MGroup title="Accesso" note={sonoIo ? 'È il tuo utente: ruolo e accesso li cambia un altro amministratore.' : undefined}>
        <MRow title="Ruolo" trail={u.role.name} trailTone="mute" onClick={scrive && !sonoIo ? () => apri('ruolo', u.role.id) : undefined} chevron={scrive && !sonoIo} />
        {scrive && !sonoIo && (
          <button
            type="button"
            className={`stm-azione${u.active ? ' is-danger' : ''}`}
            disabled={salva.isPending}
            onClick={async () => {
              if (u.active && !(await confirmDialog({ title: `Disattivare ${u.firstName} ${u.lastName}?`, body: 'Non potrà più entrare nel gestionale finché non lo riattivi.', danger: true, confirmLabel: 'Disattiva' }))) return;
              salva.mutate({ active: !u.active });
            }}
          >
            <Icon name={u.active ? 'lock' : 'check'} size={15} /> {u.active ? "Disattiva l'utente" : "Riattiva l'utente"}
          </button>
        )}
        {admin && !sonoIo && (
          <button
            type="button"
            className="stm-azione"
            onClick={() => useModalStore.getState().open('password', { userId: u.id, userName: `${u.firstName} ${u.lastName}`, mode: 'admin' })}
          >
            <Icon name="refresh" size={15} /> Reimposta la password
          </button>
        )}
      </MGroup>

      <MCampo
        open={aperto === 'nome' || aperto === 'cognome' || aperto === 'email'}
        onClose={() => setAperto(null)}
        titolo={aperto === 'email' ? 'Email' : aperto === 'cognome' ? 'Cognome' : 'Nome'}
        nota={aperto === 'email' ? "È anche l'indirizzo con cui entra nel gestionale." : undefined}
        campo={campo(aperto === 'email' ? 'email' : 'text')}
        onSalva={() => {
          const v = bozza.trim();
          if (!v) return;
          salva.mutate(aperto === 'email' ? { email: v } : aperto === 'cognome' ? { lastName: v } : { firstName: v });
        }}
      />
      <MScelte
        open={aperto === 'ruolo'}
        onClose={() => setAperto(null)}
        titolo="Ruolo"
        nota="Il ruolo decide quali sezioni vede e cosa può modificare."
        opzioni={(roles ?? []).map((r) => ({ v: r.id, l: r.name, sub: r.canSeeMargin ? 'vede anche i margini' : undefined }))}
        valore={u.role.id}
        onScegli={(v) => { if (v !== u.role.id) salva.mutate({ roleId: v }); else setAperto(null); }}
      />
    </>
  );
}

// ── Ruoli e permessi ──────────────────────────────────────────────────────

function Ruoli({ onApri }: { onApri: (id: string) => void }) {
  const { data: roles } = useQuery(rolesQuery);
  const ordinati = [...(roles ?? [])].sort((a, b) => b.usersCount - a.usersCount || a.name.localeCompare(b.name, 'it'));
  return (
    <>
      <MHead title="Ruoli e permessi" sub="Ogni ruolo si apre con le sezioni che vede e cosa può farci." />
      <MGroup title="Ruoli" count={ordinati.length || undefined}>
        {ordinati.map((r) => (
          <MRow
            key={r.id}
            text={r.shortLabel}
            title={r.name}
            sub={[r.usersCount === 0 ? 'nessun utente' : r.usersCount === 1 ? '1 utente' : `${r.usersCount} utenti`, r.canSeeMargin ? 'vede i margini' : null, r.isSystem ? null : 'personalizzato'].filter(Boolean).join(' · ')}
            onClick={() => onApri(r.id)}
          />
        ))}
        {!roles && <MEmpty>Carico i ruoli…</MEmpty>}
      </MGroup>
    </>
  );
}

function Ruolo({ id }: { id: string }) {
  const qc = useQueryClient();
  const { scrive } = useLivelloImpostazioni();
  const { data: roles } = useQuery(rolesQuery);
  const [modulo, setModulo] = useState<string | null>(null);
  const r = roles?.find((x) => x.id === id);

  // Lo stesso aggiornamento della matrice del computer, ottimistico.
  const updateLevel = useMutation({
    mutationFn: ({ roleId, module, level }: { roleId: string; module: string; level: string }) =>
      api.patch(`/settings/roles/${roleId}/permissions/${module}`, { level }),
    onMutate: async ({ roleId, module, level }) => {
      await qc.cancelQueries({ queryKey: ['roles'] });
      const prev = qc.getQueryData<Role[]>(['roles']);
      if (prev) {
        qc.setQueryData<Role[]>(['roles'], prev.map((x) => {
          if (x.id !== roleId) return x;
          const altri = x.permissions.filter((p) => p.module !== module);
          return { ...x, permissions: level === 'NONE' ? altri : [...altri, { module, level }] };
        }));
      }
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['roles'], ctx.prev);
      toast.alert('Permesso non aggiornato', e?.message ?? 'Riprova');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  });

  if (!roles) return <MEmpty>Carico il ruolo…</MEmpty>;
  if (!r) return <MEmpty>Questo ruolo non c'è più.</MEmpty>;
  // Il Super Admin non si modifica, come sul computer.
  const bloccato = r.isSystem && r.key === 'SUPER_ADMIN';
  const livello = (m: string) => r.permissions.find((p) => p.module === m)?.level ?? 'NONE';
  const modificabile = scrive && !bloccato;

  return (
    <>
      <MHead
        title={r.name}
        sub={[r.usersCount === 1 ? '1 utente' : `${r.usersCount} utenti`, r.canSeeMargin ? 'vede i margini' : 'non vede i margini', r.isSystem ? 'ruolo di sistema' : 'personalizzato'].join(' · ')}
      />
      <MGroup
        title="Cosa vede"
        note={bloccato ? 'Il Super Admin vede e gestisce tutto, e non si modifica.' : modificabile ? 'Tocca una sezione per cambiare cosa può farci chi ha questo ruolo.' : undefined}
      >
        {MODULI.map(([m, nome]) => {
          const lvl = livello(m);
          return (
            <MRow
              key={m}
              title={nome}
              trail={nomeLivello(lvl)}
              trailTone={lvl === 'NONE' ? 'mute' : undefined}
              onClick={modificabile ? () => setModulo(m) : undefined}
              chevron={modificabile}
            />
          );
        })}
      </MGroup>
      <MScelte
        open={!!modulo}
        onClose={() => setModulo(null)}
        titolo={modulo ? (MODULI.find(([m]) => m === modulo)?.[1] ?? modulo) : ''}
        nota={`Per chi ha il ruolo ${r.name}.`}
        opzioni={LIVELLI}
        valore={modulo ? livello(modulo) : ''}
        onScegli={(v) => {
          const m = modulo;
          setModulo(null);
          if (m && v !== livello(m)) updateLevel.mutate({ roleId: r.id, module: m, level: v });
        }}
      />
    </>
  );
}

// ── Preferenze ────────────────────────────────────────────────────────────

type CampoOrg = keyof OrgSettings;

function Preferenze() {
  const qc = useQueryClient();
  const { scrive } = useLivelloImpostazioni();
  const { data: o } = useQuery(orgQuery);
  const [aperto, setAperto] = useState<CampoOrg | null>(null);
  const [bozza, setBozza] = useState('');

  // Lo stesso salvataggio del computer, un campo alla volta.
  const salva = useMutation({
    mutationFn: (data: Partial<OrgSettings>) => api.patch('/settings/org', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-settings'] });
      setAperto(null);
      toast.ok('Preferenza salvata');
    },
    onError: (e: any) => toast.alert('Salvataggio non riuscito', e?.message ?? 'Riprova'),
  });

  if (!o) return <MEmpty>Carico le preferenze…</MEmpty>;
  const apri = (k: CampoOrg, v: string) => { if (!scrive) return; setBozza(v); setAperto(k); };
  const riga = (k: CampoOrg, titolo: string, valore: string, sub?: string) => (
    <MRow title={titolo} sub={sub} trail={valore} trailTone="mute" onClick={scrive ? () => apri(k, String(o[k] ?? '')) : undefined} />
  );
  const scelte = (k: CampoOrg, titolo: string, opzioni: Array<{ v: string; l: string; sub?: string }>, nota?: string) => (
    <MScelte
      open={aperto === k}
      onClose={() => setAperto(null)}
      titolo={titolo}
      nota={nota}
      opzioni={opzioni}
      valore={String(o[k] ?? '')}
      onScegli={(v) => salva.mutate({ [k]: k === 'fiscalYearStart' ? Number(v) : v } as Partial<OrgSettings>)}
    />
  );
  const testo = (k: CampoOrg, titolo: string, input: ReactNode, nota?: string, togli?: boolean) => (
    <MCampo
      open={aperto === k}
      onClose={() => setAperto(null)}
      titolo={titolo}
      nota={nota}
      campo={input}
      onSalva={() => {
        const v = bozza.trim();
        if (k === 'organizationName' && !v) return;
        salva.mutate({ [k]: k === 'logoUrl' ? (v || null) : v } as Partial<OrgSettings>);
      }}
      onTogli={togli && o[k] ? () => salva.mutate({ [k]: null } as Partial<OrgSettings>) : undefined}
      togli="Togli il logo"
    />
  );

  return (
    <>
      <MHead title="Preferenze" sub="Valgono per tutta l'organizzazione e per i documenti." />
      <MGroup title="Identità">
        {riga('organizationName', 'Nome', o.organizationName)}
        {riga('logoUrl', 'Logo', o.logoUrl ? 'impostato' : 'nessuno', o.logoUrl ?? undefined)}
        {riga('primaryColor', 'Colore principale', o.primaryColor)}
      </MGroup>
      <MGroup title="Conti e formati">
        {riga('marginThreshold', 'Soglia del margine', `${Number(o.marginThreshold).toFixed(0)}%`, 'sotto, il progetto va in allerta')}
        {riga('currency', 'Valuta', o.currency)}
        {riga('timezone', 'Fuso orario', o.timezone)}
        {riga('dateFormat', 'Formato delle date', o.dateFormat)}
        {riga('docLanguage', 'Lingua dei documenti', o.docLanguage === 'it' ? 'italiano' : 'inglese')}
        {riga('fiscalYearStart', "Inizio dell'anno fiscale", MESI[Math.max(0, Math.min(11, o.fiscalYearStart - 1))] ?? String(o.fiscalYearStart))}
      </MGroup>

      {testo('organizationName', 'Nome', <input className="inp" autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Nome dell'organizzazione" />)}
      {testo('logoUrl', 'Logo', <input className="inp" type="url" inputMode="url" autoFocus placeholder="https://…" value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Indirizzo del logo" />, "L'indirizzo dell'immagine: finisce sui documenti.", true)}
      {testo('primaryColor', 'Colore principale', <input className="inp mono" autoFocus placeholder="#050519" value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Colore principale" />, 'In esadecimale, come #050519.')}
      {testo('marginThreshold', 'Soglia del margine', <input className="inp mono" type="number" inputMode="numeric" min={0} max={100} autoFocus value={bozza} onChange={(e) => setBozza(e.target.value)} aria-label="Soglia del margine in percentuale" />, 'In percentuale: un progetto con un margine più basso va in allerta.')}
      {scelte('currency', 'Valuta', ['EUR', 'USD', 'GBP', 'CHF'].map((v) => ({ v, l: v })))}
      {scelte('timezone', 'Fuso orario', ['Europe/Rome', 'Europe/London', 'Europe/Berlin', 'UTC'].map((v) => ({ v, l: v })))}
      {scelte('dateFormat', 'Formato delle date', [
        { v: 'dd/MM/yyyy', l: 'dd/MM/yyyy', sub: '31/10/2026' },
        { v: 'MM/dd/yyyy', l: 'MM/dd/yyyy', sub: '10/31/2026' },
        { v: 'yyyy-MM-dd', l: 'yyyy-MM-dd', sub: '2026-10-31' },
      ])}
      {scelte('docLanguage', 'Lingua dei documenti', [{ v: 'it', l: 'Italiano' }, { v: 'en', l: 'Inglese' }])}
      {scelte('fiscalYearStart', "Inizio dell'anno fiscale", MESI.map((m, i) => ({ v: String(i + 1), l: m.charAt(0).toUpperCase() + m.slice(1) })))}
    </>
  );
}

// ── Servizi collegati ─────────────────────────────────────────────────────

function Integrazioni() {
  const qc = useQueryClient();
  const { data: fic } = useQuery(ficStatusQuery);
  const { data: qonto } = useQuery(qontoStatusQuery);
  const { data: altre } = useQuery({ queryKey: ['integrations'], queryFn: () => api.get<Integration[]>('/settings/integrations') });
  const [aperta, setAperta] = useState<Integration | null>(null);

  // Lo stesso aggiornamento della scheda Qonto del computer (e del menu della Situazione).
  const sync = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; stats?: { created?: number; updated?: number } } | undefined>('/admin/sync/qonto'),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['qonto-status'] });
      qc.invalidateQueries({ queryKey: ['admin'] });
      const s = r?.stats;
      toast.ok('Qonto aggiornato', s ? `${s.created ?? 0} movimenti nuovi · ${s.updated ?? 0} aggiornati` : undefined);
    },
    onError: (e: any) => toast.alert('Aggiornamento non riuscito', e?.message ?? 'Riprova'),
  });

  return (
    <>
      <MHead title="Servizi collegati" sub="Da qui arrivano fatture, saldi e movimenti." />

      <MGroup title="FattureInCloud">
        {!fic ? <MEmpty>Controllo il collegamento…</MEmpty> : fic.connected ? (
          <>
            <MRow icon="invoice" title={fic.companyName ?? 'Azienda collegata'} sub={fic.companyId ? `azienda ${fic.companyId}` : undefined} trail="collegato" trailTone="mute" />
            <MRow title="Ultimo aggiornamento" trail={quando(fic.lastSyncAt)} trailTone="mute" />
            <MRow title="Collegato il" trail={giorno(fic.connectedAt) ?? '—'} trailTone="mute" />
          </>
        ) : (
          <MRow icon="invoice" tone="alert" title="Non collegato" sub="senza, fatture e preventivi non arrivano" />
        )}
      </MGroup>

      <MGroup title="Qonto">
        {!qonto ? <MEmpty>Controllo il collegamento…</MEmpty> : qonto.connected ? (
          <>
            <MRow
              icon="landmark"
              title={qonto.accounts?.length ? (qonto.accounts.length === 1 ? '1 conto' : `${qonto.accounts.length} conti`) : 'Conto collegato'}
              sub={qonto.accounts?.length ? qonto.accounts.map((a) => a.name).join(', ') : undefined}
              trail="collegato"
              trailTone="mute"
            />
            <MRow title="Ultimo aggiornamento" trail={quando(qonto.lastSyncAt)} trailTone="mute" />
            <MRow title="Collegato il" trail={giorno(qonto.connectedAt) ?? '—'} trailTone="mute" />
            <button type="button" className="stm-azione" disabled={sync.isPending} onClick={() => sync.mutate()}>
              <Icon name="refresh" size={15} /> {sync.isPending ? 'Aggiorno…' : 'Aggiorna adesso saldi e movimenti'}
            </button>
          </>
        ) : (
          <MRow icon="landmark" tone="alert" title="Non collegato" sub="senza, saldi e movimenti non arrivano in Amministrazione" />
        )}
      </MGroup>

      <MGroup title="Altri servizi" count={altre?.length || undefined}>
        {(altre ?? []).length ? (altre ?? []).map((i) => (
          <MRow
            key={i.id}
            icon="link"
            tone={i.status === 'ERROR' ? 'alert' : undefined}
            title={i.name}
            sub={[(KIND[i.kind] ?? i.kind) !== i.name ? KIND[i.kind] ?? i.kind : null, STATO_INT[i.status] ?? i.status.toLowerCase()].filter(Boolean).join(' · ')}
            onClick={() => setAperta(i)}
          />
        )) : <MEmpty>Nessun altro servizio collegato.</MEmpty>}
      </MGroup>

      <DesktopOnly
        cosa="Collegare, scollegare o rinnovare un servizio"
        perche="servono chiavi e token da incollare e verificare con calma."
      ><></></DesktopOnly>

      <MobileSheet open={!!aperta} onClose={() => setAperta(null)} title={aperta?.name}>
        {aperta && (
          <div className="mk-list">
            <MRow title="Stato" trail={STATO_INT[aperta.status] ?? aperta.status.toLowerCase()} trailTone={aperta.status === 'ERROR' ? 'alert' : 'mute'} />
            <MRow title="Chiave" trail={aperta.hasKey ? 'configurata' : 'mancante'} trailTone={aperta.hasKey ? 'mute' : 'alert'} />
            {aperta.lastSyncAt && <MRow title="Ultimo aggiornamento" trail={quando(aperta.lastSyncAt)} trailTone="mute" />}
            {aperta.connectedBy && <MRow title="Collegata da" trail={`${aperta.connectedBy.firstName} ${aperta.connectedBy.lastName}`} trailTone="mute" />}
          </div>
        )}
      </MobileSheet>
    </>
  );
}
